// adminfrontend/src/admin_ui/pages/AdminComplianceShipmentsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import adminComplianceApi from "../services/adminComplianceApi";
import "../styles/Admin.css";

function asArr(v) {
  return Array.isArray(v) ? v : [];
}

export default function AdminComplianceShipmentsPage({ adminEmail, onLogout }) {
  const [status, setStatus] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const normalized = useMemo(() => asArr(rows), [rows]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const params = {
        status: status || undefined,
        pendingStatus: pendingStatus || undefined,
        pendingAction: pendingAction || undefined,
        q: q.trim() || undefined,
        limit,
        offset,
      };
      const res = await adminComplianceApi.shipments(params);
      if (!res?.ok) throw new Error(res?.error || "load_failed");

      const list = res.rows ?? res.shipments ?? res.items ?? [];
      setRows(Array.isArray(list) ? list : []);
      setTotal(Number(res.total ?? (Array.isArray(list) ? list.length : 0) ?? 0));
    } catch (e) {
      console.error("[AdminComplianceShipmentsPage] load error:", e);
      setRows([]);
      setTotal(0);
      setError("Unable to load compliance shipments.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function nextPage() {
    setOffset((v) => v + limit);
  }
  function prevPage() {
    setOffset((v) => Math.max(0, v - limit));
  }

  useEffect(() => {
    // reload when paging changes
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Compliance — Shipments</h1>
            <p className="ucx-admin-subtitle">Shipment status pipeline + pending approval signals.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={load} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>
          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <input
              className="ucx-admin-input-compact"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search shipment no / id"
              style={{ minWidth: 220 }}
            />
            <input
              className="ucx-admin-input-compact"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              placeholder="Status (draft, shipped, delivered...)"
              style={{ minWidth: 220 }}
            />
            <input
              className="ucx-admin-input-compact"
              value={pendingStatus}
              onChange={(e) => setPendingStatus(e.target.value)}
              placeholder="Pending status (pending/...)"
              style={{ minWidth: 220 }}
            />
            <input
              className="ucx-admin-input-compact"
              value={pendingAction}
              onChange={(e) => setPendingAction(e.target.value)}
              placeholder="Pending action (CREATE/UPDATE/...)"
              style={{ minWidth: 240 }}
            />
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button type="button" className="ucx-btn ucx-btn-primary" onClick={() => { setOffset(0); load(); }} disabled={loading}>
              Apply
            </button>
            <span className="ucx-small">Showing {normalized.length} / {total}</span>
          </div>

          {error && <div className="ucx-admin-status-error">{error}</div>}
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Shipment #</th>
                <th>Status</th>
                <th>Pending</th>
                <th>Action</th>
                <th>Requested at</th>
                <th>Origin</th>
                <th>Destination</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="ucx-admin-empty">Loading…</td></tr>
              )}

              {!loading && normalized.length === 0 && (
                <tr><td colSpan={8} className="ucx-admin-empty">No shipments found.</td></tr>
              )}

              {normalized.map((s) => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td>{s.shipment_no ?? s.shipmentNo ?? "-"}</td>
                  <td>{s.status ?? "-"}</td>
                  <td>{s.pending_status ?? s.pendingStatus ?? "-"}</td>
                  <td>{s.pending_action ?? s.pendingAction ?? "-"}</td>
                  <td>
                    {s.pending_requested_at
                      ? new Date(s.pending_requested_at).toLocaleString()
                      : s.created_at
                      ? new Date(s.created_at).toLocaleString()
                      : "-"}
                  </td>
                  <td>{s.origin_text ?? s.origin ?? s.origin_location_text ?? "-"}</td>
                  <td>{s.destination_text ?? s.destination ?? s.destination_location_text ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ucx-admin-toolbar">
          <div className="ucx-admin-toolbar-hint">
            Page: {Math.floor(offset / limit) + 1}
          </div>
          <div className="ucx-admin-form-actions">
            <button type="button" className="ucx-btn ucx-btn-outline" onClick={prevPage} disabled={loading || offset <= 0}>
              Previous
            </button>
            <button type="button" className="ucx-btn ucx-btn-primary" onClick={nextPage} disabled={loading || normalized.length < limit}>
              Next
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
